import React, { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { BrowserRouter as Router } from 'react-router-dom';
import AppRoutes from './routes';
import Sidebar from './components/Sidebar';
import { ErrorBoundary, LoadingSpinner, AuthErrorScreen } from './components/feedback';
import SessionTimeoutProvider from './components/SessionTimeoutProvider';
import ThemeProvider from './components/ThemeProvider';
import { SkipLink, AnnouncerProvider } from './components/a11y';
import { useAuthErrorInfo } from './hooks/useAuthErrorInfo';

const App: React.FC = () => {
  const [isMounted, setIsMounted] = useState(false);
  const { loginWithRedirect, logout } = useAuth0();
  const authErrorInfo = useAuthErrorInfo();

  useEffect(() => {
    setIsMounted(true);
  }, []);

  if (!isMounted) {
    return (
      <div className="min-h-screen bg-au-body-updated flex items-center justify-center">
        <LoadingSpinner size="lg" label="Loading application..." />
      </div>
    );
  }

  // Full takeover, no Sidebar and no routes mounted at all — a user in a
  // broken auth state (blocked SSO, denied account, lockout, broken token
  // exchange) should never see any app navigation or stale page content.
  if (authErrorInfo) {
    return (
      <ThemeProvider>
        <AuthErrorScreen
          errorInfo={authErrorInfo}
          onRetry={() => loginWithRedirect()}
          onReset={() => logout({ logoutParams: { returnTo: window.location.origin } })}
        />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider>
      <Router>
        <AnnouncerProvider>
          <SessionTimeoutProvider>
            <div className="app-layout min-h-screen bg-au-body-updated transition-colors duration-200">
              <Sidebar />
              <main id="main-content" className="app-main au-dashboard" tabIndex={-1}>
                <ErrorBoundary>
                  <AppRoutes />
                </ErrorBoundary>
              </main>
            </div>
          </SessionTimeoutProvider>
        </AnnouncerProvider>
      </Router>
    </ThemeProvider>
  );
};

export default App;
